import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import type {
  UseFormProps,
  FieldValues,
  SubmitHandler,
  Resolver,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { z } from 'zod'
import { useApiErrorHandler } from '#/shared/lib/api/error-handler'
import type { FormSubmissionState } from './types'

/**
 * Enhanced form hook with Zod validation and submission state
 */
export const useFormWithZod = <TOutput extends FieldValues>(
  schema: z.ZodType<TOutput>,
  formOptions?: Omit<UseFormProps<TOutput>, 'resolver'>,
) => {
  const form = useForm<TOutput>({
    // Zod v4 types don't fully align with @hookform/resolvers generics
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema as any) as Resolver<TOutput>,
    ...formOptions,
  })
  const { handleError } = useApiErrorHandler()

  const [submissionState, setSubmissionState] = useState<FormSubmissionState>({
    isSubmitting: false,
    isSubmitSuccessful: false,
    submitError: null,
  })

  const handleSubmit = useCallback(
    (onSubmit: SubmitHandler<TOutput>) =>
      form.handleSubmit(async (data) => {
        setSubmissionState({
          isSubmitting: true,
          isSubmitSuccessful: false,
          submitError: null,
        })

        try {
          await onSubmit(data)
          setSubmissionState({
            isSubmitting: false,
            isSubmitSuccessful: true,
            submitError: null,
          })
        } catch (error) {
          const errorMessage = handleError(error, 'Form submission')
          setSubmissionState({
            isSubmitting: false,
            isSubmitSuccessful: false,
            submitError: errorMessage,
          })
        }
      }),
    [form, handleError],
  )

  const resetSubmission = useCallback(() => {
    setSubmissionState({
      isSubmitting: false,
      isSubmitSuccessful: false,
      submitError: null,
    })
  }, [])

  return {
    ...form,
    handleSubmit,
    submissionState,
    resetSubmission,
    // Convenience getters
    isSubmitting: submissionState.isSubmitting,
    isSubmitSuccessful: submissionState.isSubmitSuccessful,
    submitError: submissionState.submitError,
  }
}

/**
 * Enhanced form hook with error handling and submission state (legacy without Zod)
 */
export const useFormWithSubmission = <
  TFieldValues extends FieldValues = FieldValues,
>(
  formOptions?: UseFormProps<TFieldValues>,
) => {
  const form = useForm<TFieldValues>(formOptions)
  const { handleError } = useApiErrorHandler()

  const [submissionState, setSubmissionState] = useState<FormSubmissionState>({
    isSubmitting: false,
    isSubmitSuccessful: false,
    submitError: null,
  })

  const handleSubmit = useCallback(
    (onSubmit: SubmitHandler<TFieldValues>) =>
      form.handleSubmit(async (data) => {
        setSubmissionState({
          isSubmitting: true,
          isSubmitSuccessful: false,
          submitError: null,
        })

        try {
          await onSubmit(data)
          setSubmissionState({
            isSubmitting: false,
            isSubmitSuccessful: true,
            submitError: null,
          })
        } catch (error) {
          const errorMessage = handleError(error, 'Form submission')
          setSubmissionState({
            isSubmitting: false,
            isSubmitSuccessful: false,
            submitError: errorMessage,
          })
        }
      }),
    [form, handleError],
  )

  const resetSubmission = useCallback(() => {
    setSubmissionState({
      isSubmitting: false,
      isSubmitSuccessful: false,
      submitError: null,
    })
  }, [])

  return {
    ...form,
    handleSubmit,
    submissionState,
    resetSubmission,
    // Convenience getters
    isSubmitting: submissionState.isSubmitting,
    isSubmitSuccessful: submissionState.isSubmitSuccessful,
    submitError: submissionState.submitError,
  }
}

/**
 * Hook for form field error messages
 */
export const useFormFieldError = (
  errors: Record<string, { message?: string } | undefined>,
  name: string,
) => {
  return errors[name]?.message ?? null
}
